import { ref } from 'vue'
import { api, endpoints } from '../../services/api.js'
import { messagePourVisiteur } from '../../utils/errorMessages.js'

/**
 * Store singleton du référentiel : catégories de formations, leurs compétences
 * et leur matériel pédagogique.
 *
 * Remplace les constantes statiques `CATEGORIES_COMPETENCES` et
 * `MATERIEL_PAR_CATEGORIE` : la source de vérité est la base (tables
 * formation_categorie / competences / materiels), éditable par l'admin depuis
 * l'écran « Catégories de formations ».
 *
 * L'état est partagé au niveau module (chargé une seule fois, mis en cache) et
 * exposé sous la FORME de l'ancienne constante — `{ id, label, sousCategories:
 * [{ id, label }] }` — pour limiter les changements côté consommateurs. `id`
 * reste le CODE de la catégorie : c'est lui que stockent les déclarations des
 * formateurs et `Mission.categorieFormation`.
 *
 * Endpoint public (sans auth) → utilisable aussi bien dans le BO que sur le
 * formulaire d'inscription formateur public.
 */

// --- État module (singleton) --------------------------------------------
const categories = ref([])
// Matériel demandé à tous, indépendamment des catégories déclarées.
const materielsCommuns = ref([])
const loaded = ref(false)
const loading = ref(false)
// Message du dernier échec de chargement, vide sinon. `loadCompetencesReferentiel`
// avalait ses erreurs (console.error puis retour du cache) : un consommateur ne
// pouvait pas distinguer « rien dans le référentiel » d'« impossible à lire ».
// Sur AppSettingsForprevSection notamment, la confusion affirmait à tort que le
// numéro FORPREV n'était demandé nulle part.
const error = ref('')
let loadPromise = null

function mapCategorie(categorie) {
  return {
    id: categorie.code,
    categorieId: categorie.id,
    slug: categorie.slug || null,
    label: categorie.nom,
    icon: categorie.icon || null,
    color: categorie.color || null,
    backgroundColor: categorie.backgroundColor || null,
    order: categorie.order ?? 0,
    sousCategories: (categorie.competences || []).map((sc) => ({
      id: sc.code,
      label: sc.nomComplet,
      nomCourt: sc.nomCourt,
      icon: sc.icon || null,
      order: sc.order ?? 0,
      // Habilitation INRS : cocher cette compétence fait apparaître la question
      // du numéro FORPREV dans le formulaire d'inscription formateur.
      requiertForprev: Boolean(sc.requiertForprev),
    })),
    materiels: (categorie.materiels || []).map(mapMateriel),
  }
}

/**
 * Le matériel garde la forme attendue par les formulaires : `id` = code (celui
 * que stocke `formateur_materiel.reference`), plus les drapeaux qui décident des
 * champs affichés à côté de la case à cocher.
 */
function mapMateriel(m) {
  return {
    id: m.code,
    materielId: m.id,
    label: m.label,
    groupe: m.groupe || null,
    avecNombre: m.avecNombre,
    nombrePlaceholder: m.nombrePlaceholder || null,
    avecModele: m.avecModele,
    avecCapacite: m.avecCapacite,
    capaciteHint: m.capaciteHint || null,
    avecPrecision: m.avecPrecision,
    precisionPlaceholder: m.precisionPlaceholder || null,
    order: m.order ?? 0,
  }
}

/**
 * Charge le référentiel depuis l'API (idempotent : renvoie le cache si déjà
 * chargé, ou la promesse en cours si un chargement est en vol). `force` refait
 * la requête (ex. après édition admin). Défini au niveau module pour être
 * réutilisable par `ensureLoaded` (auto-chargement).
 */
async function loadCompetencesReferentiel(force = false) {
  if (loaded.value && !force) return categories.value
  if (loadPromise && !force) return loadPromise
  // Résolution défensive de l'URL : si les endpoints ne sont pas disponibles
  // (ex. environnement de test qui mocke partiellement `endpoints`), on ne tente
  // pas d'appel — on renvoie le cache courant sans jeter.
  const url = endpoints?.competencesReferentiel?.public?.()
  if (!url) return categories.value
  loading.value = true
  loadPromise = api
    .get(url)
    .then((res) => {
      categories.value = (res?.categories || []).map(mapCategorie)
      materielsCommuns.value = (res?.materielsCommuns || []).map(mapMateriel)
      loaded.value = true
      error.value = ''
      return categories.value
    })
    .catch((err) => {
      console.error('Erreur lors du chargement du référentiel des compétences:', err)
      error.value = messagePourVisiteur(err, 'Le référentiel des compétences n’a pas pu être lu.')
      return categories.value
    })
    .finally(() => {
      loading.value = false
      loadPromise = null
    })
  return loadPromise
}

// Déclenche un chargement paresseux (fire-and-forget) au premier besoin : ainsi tout
// consommateur (composant OU simple getter de libellé) obtient la donnée sans devoir
// câbler un onMounted, quel que soit son contexte d'auth. Les vues réactives se
// mettent à jour dès que la donnée arrive.
function ensureLoaded() {
  // Fire-and-forget : on isole toute erreur (y compris un throw synchrone si
  // `api`/`endpoints` sont indisponibles) pour ne jamais produire de rejet non géré.
  if (!loaded.value && !loadPromise) {
    try {
      Promise.resolve(loadCompetencesReferentiel()).catch(() => {})
    } catch {
      /* no-op */
    }
  }
}

// --- Getters (auto-chargement + fallback sur le code si inconnu) ---------
const getCategory = (code) => {
  ensureLoaded()
  return categories.value.find((c) => c.id === code) || null
}
const getCategoryLabel = (code) => getCategory(code)?.label || code
const getCategoryIcon = (code) => getCategory(code)?.icon || null
const getSousCategorie = (categorieCode, sousCategorieCode) =>
  getCategory(categorieCode)?.sousCategories.find((s) => s.id === sousCategorieCode) || null
const getSousCategorieLabel = (categorieCode, sousCategorieCode) =>
  getSousCategorie(categorieCode, sousCategorieCode)?.label || sousCategorieCode

/** Matériel d'une catégorie (tableau vide tant que le référentiel n'est pas là). */
const getMateriels = (code) => {
  ensureLoaded()
  return getCategory(code)?.materiels || []
}

/** Retrouve un matériel par son code, en cherchant partout (commun compris). */
const getMateriel = (code) => {
  ensureLoaded()
  const commun = materielsCommuns.value.find((m) => m.id === code)
  if (commun) return commun
  for (const categorie of categories.value) {
    const trouve = categorie.materiels.find((m) => m.id === code)
    if (trouve) return trouve
  }
  return null
}

export function useCompetencesReferentiel() {
  // Auto-charge dès qu'un composant utilise le store (sans onMounted requis).
  ensureLoaded()

  return {
    categories,
    materielsCommuns,
    loaded,
    loading,
    error,
    loadCompetencesReferentiel,
    getCategory,
    getCategoryLabel,
    getCategoryIcon,
    getSousCategorie,
    getSousCategorieLabel,
    getMateriels,
    getMateriel,
  }
}
